import { defineStore } from 'pinia'
import { AppGlobalI, OrganizationI, OrganizationPermissionI } from '@/Types/types'
import { StripeSubscriptionI } from '@/Types/stripeTypes'
import { NavbarStore } from '@/Pinia/NavbarStore'
import { isEmpty, isNull } from 'lodash'
import { usePage } from '@inertiajs/vue3'

export interface DocuStoreI {
    organizations: Record<number, OrganizationI>
    permissions: Record<number, OrganizationPermissionI>
    activeOrganization: OrganizationI | null
}

export const DocuStore = defineStore('docu-store', {
    //data
    state: (): DocuStoreI => ({
        organizations: {},
        permissions: {},
        activeOrganization: null,
    }),
    //methods
    actions: {
        setOrganizations(orgs: OrganizationI[]) {
            orgs.map((o) => (this.organizations[o.id] = o))
        },
        setActiveOrganization(org: OrganizationI | null) {
            const navbarStore = NavbarStore()
            this.activeOrganization = org

            if (isNull(org) || isEmpty(org)) {
                navbarStore.displayAllNavbar(false)
            } else {
                navbarStore.setNavbarItemVisibility('Contact Us', false)
                navbarStore.setNavbarItemVisibility('Businesses', this.permissions[org.id]['business_view'])
                navbarStore.setNavbarItemVisibility('Projects', this.permissions[org.id]['project_view'])
                navbarStore.setNavbarItemVisibility('Collections', this.permissions[org.id]['folder_view'])
                navbarStore.setNavbarItemVisibility(
                    'Users',
                    this.permissions[org.id]['biz_invite_view'] || this.permissions[org.id]['org_invite_view'],
                )
            }
        },
        setOrganization(org: OrganizationI) {
            this.organizations[org.id] = org
        },
        setPermissions(permissions: Record<number, OrganizationPermissionI>) {
            this.permissions = permissions
        },
        setupGlobal() {
            const pageProps = usePage<{ globals: AppGlobalI }>().props
            this.setPermissions(pageProps.globals.permissions)
            this.setOrganizations(pageProps.globals.organizations)
            this.setActiveOrganization(pageProps.globals.current_organization)
        },
        deleteOrganization(id: number) {
            const navbarStore = NavbarStore()

            if (this.activeOrganization && this.activeOrganization.id === id) {
                navbarStore.displayAllNavbar(false)
                this.activeOrganization = null
            }
            delete this.permissions[id]
            delete this.organizations[id]
        },
        resetDocuStore() {
            this.organizations = {}
            this.permissions = {}
            this.setActiveOrganization(null)
        },
        updateSubscription(orgId: number, subscription: StripeSubscriptionI) {
            if (this.organizations[orgId]) {
                this.organizations[orgId].subscription = subscription

                if (this.activeOrganization && this.activeOrganization.id === orgId) {
                    this.activeOrganization.subscription = subscription
                }
            }
        },
        decrementUsersRequestingAccessCount(orgId: number, value: number = 1) {
            if (this.organizations[orgId]) {
                this.organizations[orgId].users_requesting_access_count = Math.max(
                    0,
                    this.organizations[orgId].users_requesting_access_count - value,
                )
                if (this.activeOrganization && this.activeOrganization.id === orgId) {
                    this.activeOrganization.users_requesting_access_count =
                        this.organizations[orgId].users_requesting_access_count
                }
            }
        },
    },
    //computed
    getters: {
        getOrganization: (state) => (id: number) => {
            return state.organizations[id]
        },
        getOrganizations: (state) => {
            return Object.values(state.organizations)
        },
        getOrganizationsCount: (state) => {
            return Object.keys(state.organizations).length
        },
        getActiveOrganizationId: (state) => {
            return state.activeOrganization ? state.activeOrganization.id : 0
        },
        getOrganizationInformation: (state) => (organizationId: number, data: keyof OrganizationI) => {
            if (!state.organizations[organizationId]) {
                return false
            }
            const organizationInformation = state.organizations[organizationId]
            return organizationInformation[data] ?? false
        },
        getActiveOrganizationCount: (state) => {
            return state.activeOrganization ? Object.keys(state.activeOrganization).length : 0
        },
        getPermissionToOrganization: (state) => (permission: string) => {
            if (!state.activeOrganization) {
                return false
            }

            const orgPermissions = state.permissions[state.activeOrganization.id]
            if (!orgPermissions) {
                return false
            }

            return orgPermissions[permission] ?? false
        },
    },
})
